import { notification } from "antd";
import { useCallback } from "react";
import { SubmitHandler } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
  useAuditUploadAttachmentsMutation,
  useCreateAuditMutation,
  useUpdateAuditMutation,
} from "../../../features/api/apiSlice";
import { Audit, AuditPostValues } from "../../../types/audits";
import { formatDayJSToDateString } from "../../../utils/dates";
import { getErrorMessage } from "../../../utils/errors/errors";
import { FormAuditSchema } from "./FormAudit";

export default function useSubmit(isEditMode?: boolean, audit?: Audit) {
  const navigate = useNavigate();

  const [createAudit, { isLoading: isCreating }] = useCreateAuditMutation();
  const [updateAudit, { isLoading: isUpdating }] = useUpdateAuditMutation();
  const [postAttachments, { isLoading: isPostingAttachments }] =
    useAuditUploadAttachmentsMutation();

  const create: SubmitHandler<FormAuditSchema> = useCallback(
    async (values) => {
      try {
        const formattedValues: AuditPostValues = {
          title: values.title,
          description: values.description,
          site: Number(values.site),
          audit_type: Number(values.audit_type),
          dateAudit: formatDayJSToDateString(values.dateAudit),
          accessGivenTo: values.accessGivenTo?.map(Number),
          accessRoleGivenTo: values.accessRoleGivenTo?.map(Number),
        };

        const createdAudit = await createAudit(formattedValues).unwrap();

        notification.success({
          message: "Audit crÃ©Ã©",
          description: `L'audit ${values.title} a Ã©tÃ© crÃ©Ã© avec succÃ¨s.`,
        });

        const files: undefined | File[] = values?.attachedFiles;

        if (files?.length) {
          await postAttachments({
            auditId: createdAudit.id,
            files,
          }).unwrap();
          notification.success({
            message: "PiÃ¨ces jointes ajoutÃ©es",
          });
        }

        navigate(`/audits/${createdAudit.id}`);
      } catch (error) {
        console.error("Error creating audit", error);
        notification.error({
          message: "Erreur",
          description: getErrorMessage(error),
        });
      }
    },
    [createAudit, navigate, postAttachments],
  );

  const update: SubmitHandler<FormAuditSchema> = useCallback(
    async (values) => {
      try {
        if (!audit?.id) {
          throw new Error("Pas d'ID d'audit");
        }

        const formattedValues: AuditPostValues = {
          title: values.title,
          description: values.description,
          site: Number(values.site),
          audit_type: Number(values.audit_type),
          dateAudit: formatDayJSToDateString(values.dateAudit),
          accessGivenTo: values.accessGivenTo?.map(Number),
          accessRoleGivenTo: values.accessRoleGivenTo?.map(Number),
        };

        const updatedAudit = await updateAudit({
          auditId: audit.id,
          values: formattedValues,
        }).unwrap();

        notification.success({
          message: "Audit modifiÃ©",
          description: `L'audit ${values.title} a Ã©tÃ© modifiÃ© avec succÃ¨s.`,
        });

        const files: undefined | File[] = values?.attachedFiles;

        if (files?.length) {
          await postAttachments({
            auditId: updatedAudit.id,
            files,
          }).unwrap();
          notification.success({
            message: "PiÃ¨ces jointes ajoutÃ©es",
          });
        }

        navigate(`/audits/${updatedAudit.id}`);
      } catch (error) {
        console.error("Error updating audit", error);
        notification.error({
          message: "Erreur",
          description: getErrorMessage(error),
        });
      }
    },
    [updateAudit, audit?.id, navigate, postAttachments],
  );

  return {
    onSubmit: isEditMode ? update : create,
    isLoading: isCreating || isUpdating || isPostingAttachments,
  };
}
